import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Loader2, Building2, Users, FileText, TrendingUp, Activity } from "lucide-react";

export default function PlatformDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/platform/dashboard")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data) return null;

  const t = data.totals;
  const tiles = [
    { label: "Agencies", value: t.agencies, icon: Building2, sub: `${t.new_agencies_30d} new in 30d`, tone: "bg-emerald-400/10 text-emerald-300" },
    { label: "Caregivers", value: t.caregivers, icon: Users, sub: `${t.users} total users`, tone: "bg-sky-400/10 text-sky-300" },
    { label: "Clients", value: t.clients, icon: Users, sub: "active", tone: "bg-violet-400/10 text-violet-300" },
    { label: "Reports", value: t.reports, icon: FileText, sub: `${t.reports_last_7d} this week`, tone: "bg-amber-400/10 text-amber-300" },
    { label: "Reports / week", value: t.reports_last_7d, icon: TrendingUp, sub: "platform-wide", tone: "bg-rose-400/10 text-rose-300" },
  ];

  return (
    <div className="space-y-8" data-testid="platform-dashboard">
      <header>
        <p className="text-[11px] uppercase tracking-widest text-amber-300/80 font-bold">Platform</p>
        <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">Operations dashboard</h1>
        <p className="mt-1 text-sm text-white/60">
          Fleet-wide health, usage, and recent platform audit events.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="rounded-xl bg-white/5 border border-white/10 p-4 hover:border-white/20 transition"
            data-testid={`tile-${tile.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
          >
            <div className={`inline-flex items-center justify-center h-7 w-7 rounded-lg ${tile.tone} mb-2`}>
              <tile.icon className="h-4 w-4" />
            </div>
            <div className="text-3xl font-heading font-semibold tabular-nums">{tile.value.toLocaleString()}</div>
            <div className="text-[11px] uppercase tracking-wide text-white/40 mt-1">{tile.label}</div>
            <div className="text-xs text-white/60 mt-1.5">{tile.sub}</div>
          </div>
        ))}
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-medium flex items-center gap-2">
            <Activity className="h-4 w-4 text-amber-300" /> Recent platform events
          </h2>
          <Link
            to="/platform/audit-log"
            className="text-xs text-white/60 hover:text-white"
            data-testid="dashboard-full-audit-link"
          >
            View full audit log →
          </Link>
        </div>
        {data.recent_audits.length === 0 ? (
          <div className="rounded-xl bg-white/5 border border-white/10 p-6 text-sm text-white/60 text-center">
            No platform events recorded yet. Visit an agency or impersonate an admin to populate this feed.
          </div>
        ) : (
          <div className="rounded-xl bg-white/5 border border-white/10 divide-y divide-white/5 overflow-hidden">
            {data.recent_audits.map((e) => (
              <div key={e.id} className="px-4 py-3 grid grid-cols-12 gap-3 text-sm">
                <span className="col-span-3 text-white/50 tabular-nums text-xs">
                  {new Date(e.created_at).toLocaleString()}
                </span>
                <span className="col-span-2 text-amber-200">{e.action}</span>
                <span className="col-span-2 text-white/80 truncate">{e.actor_email}</span>
                <span className="col-span-5 text-white/50 truncate">
                  {e.target_agency_id && (
                    <Link to={`/platform/agencies/${e.target_agency_id}`} className="hover:text-white">
                      agency: {e.target_agency_id.slice(0, 8)}…
                    </Link>
                  )}
                  {e.details?.target_email && <span className="ml-2">→ {e.details.target_email}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center py-20 text-white/60">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading platform health…
    </div>
  );
}
